import type {
    ChallengeRuleConfigDto,
    ChallengePersonalRuleSetDto
} from '@core/api/ChallengeClient';
import { ChallengeType } from '@core/api/ChallengeClient';
import { autodisposer } from '@core/reactions';
import { injectTSDI } from '@core/tsdi';
import { FieldState, FormState } from 'formstate';
import { computed, makeObservable, observable, reaction } from 'mobx';
import { component } from 'tsdi';
import { ChallengeDataStore } from '../../data-store';
import {
    formErrors,
    isFormStateDirty,
    makeFormClean
} from '@components/utils/form';
import { wrapInFormField } from '@core/forms';
import { required } from '@components/utils/validators';

import { bind } from 'lodash-decorators';
import { StepStatus } from '../../store';

export type ChallengePersonalRulesFormType = ReturnType<
    PersonalRulesStore['generateForm']
>;

@component
export class PersonalRulesStore {
    constructor() {
        this.form = this.generateForm(
            this.challengeDataStore.challengeConfiguration?.rule
        );

        makeObservable(this, {
            form: observable,
            fields: computed,
            errors: computed
        });
    }

    public form: ChallengePersonalRulesFormType;
    public state: StepStatus = StepStatus.INCOMPLETE;

    private get challengeDataStore() {
        return injectTSDI(ChallengeDataStore);
    }

    private generateForm(configuration?: ChallengeRuleConfigDto) {
        return new FormState({
            leaderboardEnabled: new FieldState<boolean>(
                configuration?.personalRuleSet?.leaderboardEnabled ?? true
            ),
            strategy: new FieldState<ChallengeType>(
                configuration?.personalRuleSet?.strategyConfig?.strategy ||
                    ChallengeType.HIGH_SCORE
            ).validators(required('Select strategy')),
            pointsGoal: new FieldState<number | undefined>(
                configuration?.personalRuleSet?.strategyConfig?.pointsGoal ?? 3
            ).validators((val) => this.pointsGoalValidator(val)),
            allowedWinnersCount: new FieldState<number | undefined>(
                configuration?.personalRuleSet?.strategyConfig
                    ?.allowedWinnersCount ?? 5
            ),
            qualificationThreshold: new FieldState<number | undefined>(
                configuration?.personalRuleSet?.qualificationThreshold
            )
        });
    }

    public get fields() {
        return {
            leaderboardEnabled: wrapInFormField(this.form.$.leaderboardEnabled),
            strategy: wrapInFormField(this.form.$.strategy),
            pointsGoal: wrapInFormField(this.form.$.pointsGoal),
            allowedWinnersCount: wrapInFormField(
                this.form.$.allowedWinnersCount
            ),
            qualificationThreshold: wrapInFormField(
                this.form.$.qualificationThreshold
            )
        };
    }

    public get errors() {
        return formErrors(this.form);
    }

    public generateRequestData(
        form: ChallengePersonalRulesFormType
    ): ChallengePersonalRuleSetDto {
        const {
            $: {
                leaderboardEnabled,
                strategy,
                pointsGoal,
                allowedWinnersCount,
                qualificationThreshold
            }
        } = form;

        return {
            leaderboardEnabled: leaderboardEnabled.value,
            strategyConfig: {
                strategy: strategy.value,
                ...(strategy.value === ChallengeType.RACE && {
                    pointsGoal: pointsGoal.value
                }),
                allowedWinnersCount: allowedWinnersCount.value
            },
            qualificationThreshold: qualificationThreshold.value
        };
    }

    public get formIsDirty() {
        return isFormStateDirty(this.form.$);
    }

    @bind
    public async onChangeHandle() {
        const validation = await this.form.validate();
        if (validation.hasError) {
            this.state = StepStatus.ERROR;
        } else {
            this.state = StepStatus.MODIFIED;
        }
    }

    @bind
    public async updateForm(configuration?: ChallengeRuleConfigDto) {
        this.form = this.generateForm(configuration);
        const validation = await this.form.validate();
        if (validation.hasError) {
            this.state = StepStatus.ERROR;
            return;
        }
        this.state = StepStatus.COMPLETED;
        makeFormClean(this.form);
        return;
    }

    @bind
    private pointsGoalValidator(val: number | undefined): string | undefined {
        if (this.fields.strategy.value === ChallengeType.RACE && !val) {
            return 'Points goal is required';
        }
        return undefined;
    }

    @autodisposer.tsdi
    public initialize() {
        return [
            reaction(
                () => this.challengeDataStore.challengeConfiguration,
                async (configuration) => {
                    this.updateForm(configuration?.rule);
                }
            )
        ];
    }
}
